import React, {useEffect, useMemo, useState} from 'react';
import {Button, Checkbox, Table, TableContainer} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {useOutletContext, useSearchParams} from 'react-router-dom';

import {
  DEFAULT_PER_PAGE,
  TAX_EXTENSION_STATUS,
  themmeColor,
  VALID_CPA_EMAILS,
} from 'src/constants/constants';
import {
  exportExpressExtensionData,
  getUsersList,
  updateTaxFilingExtensionStatus,
} from 'src/appApi';
import FilterBar, {SearchPanelData} from 'src/common/FilterBar/FilterBar';
import ListTableHeader from 'src/common/Table/ListTableHeader';
import ListTableBody from 'src/common/Table/ListTableBody';
import {
  ACTION_TYPES,
  BACKEND_RESPONSE_KEYS,
  CPA_LIST_COLUMNS,
  getDateTimeFromMonthDayString,
  getMonthDateFromDate,
  Record,
  USER_ACTIVITY_TYPE,
} from 'src/CpaCenterList/components/cpaList.utils';
import CpaListContext from 'src/CpaCenterList/components/CpaListContext';
import CPAAssignCell from 'src/CpaCenterList/components/CPAAssignCell/CPAAssignCell';
import NormalReadOnlyCell from 'src/CpaCenterList/components/NormalReadOnlyCell/NormalReadOnlyCell';
import UpdateReturnStatusCell from 'src/CpaCenterList/components/UpdateReturnStatusCell/UpdateReturnStatusCell';
import ActionCell from 'src/CpaCenterList/components/ActionCell/ActionCell';
import UpdateNoteCell from 'src/CpaCenterList/components/UpdateNoteCell/UpdateNoteCell';
import ActivityCell from 'src/CpaCenterList/components/ActivityCell/ActivityCell';
import CpaListFilterModal from 'src/CpaCenterList/components/CpaListFiltersModal';
import ExportColumns from 'src/CpaCenterList/components/ExportColumns/ExportColumns';
import Pagination from 'src/common/Pagination/Pagination';
import FilledCrossIcon from 'src/icons/FilledCrossIcon';
import FractionCell from './components/FractionCell/FractionCell';
import {parse} from 'querystring';
import {ESIGN_MODE, USER_COMMS_MODE} from 'src/store/user/user.reducer';
import {Mail, Smartphone} from '@mui/icons-material';
import CpaAcknowledgementDrawer from './components/CpaAcknowledgementDrawer/CpaAcknowledgementDrawer';
import {filterFields} from './CpaCenterList.constants';
import DSCheckBox from 'src/CpaCenterV2/common/DSCheckBox/DSCheckBox';
import DSButton from 'src/DesignSystem/Button/Button';
import {NotificationType} from 'src/store/app/app.reducer';
import useNotify from 'src/DesignSystem/Notify/useNotify';
import ConfirmFileExtensionPopUp from './components/ConfirmFileExtensionPopUp';
import useLayout from 'src/CpaCenterV2/hooks/useLayout';
import {useActiveYear} from 'src/common/hooks/useActiveYear';

const CpaCenterList: React.FC = () => {
  const {activeYear} = useActiveYear();
  const {openDrawer} = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectUsersForAction, setSelectUsersForAction] = useState(false);
  const [selectedUsersList, setSelectedUsersList] = useState<number[]>([]);
  const [showCpaAckMessageDrawer, setShowCpaAckMessageDrawer] =
    useState(false);
  const [showFileExtensionConfirmation, setShowFileExtensionConfirmation] =
    useState(false);

  const defaultReverse = searchParams.get('reverse');
  const defaultSortingKey = searchParams.get('ordering');
  const defaultPlansKey = searchParams.getAll('selectedPlan');
  const defaultSelectedReturnStatus = searchParams.getAll(
    'selectedReturnStatus',
  );
  const defaultUserId = searchParams.get('userId');
  const defaultEmail = searchParams.get('email');
  const defaultName = searchParams.get('name');
  const defaultPageCount = searchParams.get('pageCount');
  const pageCount = defaultPageCount ? parseInt(defaultPageCount) : 1;

  const defaultSelectedCpaActions = searchParams.getAll('selectedCpaActions');
  const defaultSelectedReviewerActions = searchParams.getAll(
    'selectedReviewerActions',
  );
  const defaultSelectedCPAs = searchParams.getAll('selectedCPAs');
  const defaultSelectedReviewers = searchParams.getAll('selectedReviewers');
  const defaultSelectedDates = searchParams.getAll('selectedDates');
  const defaultSelectedTaxExtension = searchParams.getAll(
    'selectedTaxExtension',
  );
  const defaultSelectedActivity = searchParams.get('selectedActivity');
  const defaultSelectedTaxQueryStatus = searchParams.get(
    'selectedTaxQueryStatus',
  );
  const defaultSelectedCommsMode = searchParams.get(filterFields.COMMS_MODE);
  const defaultSelectedExpressExtensionEligible = searchParams.get(
    filterFields.EXPRESS_EXTENSION_ELIGIBLE,
  );
  const defaultSelectedCollaborator = searchParams.getAll(
    filterFields.COLLABORATOR_EMAIL,
  );
  const defaultSelectedTFSegment = searchParams.getAll(
    filterFields.TF_SEGMENT,
  );
  const defaultSelectedFiledElsewhere = searchParams.get(
    filterFields.FILED_ELSEWHERE,
  );

  const [isPrev, setIsPrev] = useState(false);
  const [isNext, setIsNext] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [recordsArray, setRecordsArray] = useState<Array<Record>>([]);
  const [totalPages, setTotalPages] = useState(1);

  const {notify} = useNotify();
  const {windowSize} = useLayout();

  const NAV_BAR_HEIGHT = 150;
  const FOOTER_HEIGHT = 60;
  const MAIN_CONTENT_HEIGHT = windowSize.height - NAV_BAR_HEIGHT;
  const USER_LIST_HEIGHT = MAIN_CONTENT_HEIGHT - FOOTER_HEIGHT;

  const getOrderingKey = () => {
    if (defaultSortingKey) {
      if (defaultReverse === 'true') {
        return {ordering: `-${defaultSortingKey}`};
      }
      return {ordering: `${defaultSortingKey}`};
    }
    return {};
  };

  const getIdEmailAndNameFilteringKeys = () => {
    let filters: {fly_user_id?: string; email?: string; name?: string} = {};
    if (defaultUserId) filters['fly_user_id'] = defaultUserId;
    if (defaultEmail) filters['email'] = defaultEmail;
    if (defaultName) filters['name'] = defaultName;
    return filters;
  };

  const getActivityFilter = () => {
    if (
      Object.values(USER_ACTIVITY_TYPE).includes(
        defaultSelectedActivity as USER_ACTIVITY_TYPE,
      )
    ) {
      return {[BACKEND_RESPONSE_KEYS.ACTIVITY]: defaultSelectedActivity};
    }
    return {};
  };

  const getTaxQueryStatusFilter = () => {
    if (defaultSelectedTaxQueryStatus === 'true') {
      return {[BACKEND_RESPONSE_KEYS.TAX_QUERY_OPEN]: true};
    }
    if (defaultSelectedTaxQueryStatus === 'false') {
      return {[BACKEND_RESPONSE_KEYS.TAX_QUERY_OPEN]: false};
    }
    return {};
  };

  const getFilterParams = () => {
    return {
      per_page: DEFAULT_PER_PAGE,
      year: activeYear,
      page: pageCount,
      status: defaultSelectedReturnStatus,
      ...getOrderingKey(),
      payment_plan: defaultPlansKey,
      ...getIdEmailAndNameFilteringKeys(),
      assigned_cpa: defaultSelectedCPAs,
      assigned_reviewer: defaultSelectedReviewers,
      cpa_action_required: defaultSelectedCpaActions,
      reviewer_action_required: defaultSelectedReviewerActions,
      next_action_date: defaultSelectedDates,
      comms_mode: defaultSelectedCommsMode,
      [BACKEND_RESPONSE_KEYS.EXTENSION_STATUS]: defaultSelectedTaxExtension,
      [BACKEND_RESPONSE_KEYS.EXPRESS_EXTENSION_ELIGIBLE]:
        defaultSelectedExpressExtensionEligible,
      collaborator: defaultSelectedCollaborator,
      [BACKEND_RESPONSE_KEYS.TF_SEGMENT]: defaultSelectedTFSegment,
      [BACKEND_RESPONSE_KEYS.FILED_ELSEWHERE]: defaultSelectedFiledElsewhere,
      ...getActivityFilter(),
      ...getTaxQueryStatusFilter(),
    };
  };

  const fetchUserList = async () => {
    try {
      setIsLoading(true);
      const params = getFilterParams();
      const response = await getUsersList(params);
      setRecordsArray(response.data.results);
      setIsPrev(!!response.data.previous);
      setIsNext(!!response.data.next);
      setTotalPages(Math.ceil(response.data.count / DEFAULT_PER_PAGE));
      setIsLoading(false);
    } catch (e) {
      alert('Something went wrong!!!, will reset filters');
      onClearFilter();
    }
  };

  const onClearFilter = () => {
    setSearchParams({});
  };

  const onClickSearchHandler = (data: SearchPanelData) => {
    setSearchParams((prevSearchParams) => {
      if (data.userId) prevSearchParams.set('userId', data.userId);
      if (data.email) prevSearchParams.set('email', data.email);
      if (data.name) prevSearchParams.set('name', data.name);
      prevSearchParams.set('pageCount', '1');
      return prevSearchParams;
    });
  };

  const closeFilterModal = () => setIsFilterModalOpen(false);
  const openFilterModal = () => setIsFilterModalOpen(true);

  const getCpaListColumnComponentMapper = () => {
    const columns = [];

    if (selectUsersForAction) {
      columns.push({
        label: '',
        render: (record: Record) => (
          <DSCheckBox
            onClick={(e) => {
              e.stopPropagation();
            }}
            onChange={(checked) => {
              if (checked) {
                setSelectedUsersList((prev) => [
                  record[BACKEND_RESPONSE_KEYS.FLYFIN_USER_ID],
                  ...prev,
                ]);
              } else {
                setSelectedUsersList((prev) =>
                  prev.filter(
                    (id) =>
                      id !== record[BACKEND_RESPONSE_KEYS.FLYFIN_USER_ID],
                  ),
                );
              }
            }}
            isChecked={selectedUsersList.includes(
              record[BACKEND_RESPONSE_KEYS.FLYFIN_USER_ID],
            )}
            disabled={isLoading || showCpaAckMessageDrawer}
          />
        ),
        backendResponseKey: null,
      });
    }

    const defaultColumns = [
      {
        label: CPA_LIST_COLUMNS.USER_ID,
        render: (record: Record) => (
          <NormalReadOnlyCell
            value={`${record[BACKEND_RESPONSE_KEYS.FLYFIN_USER_ID]}`}
          />
        ),
        backendResponseKey: BACKEND_RESPONSE_KEYS.FLYFIN_USER_ID,
      },
      {
        label: CPA_LIST_COLUMNS.NAME,
        render: (record: Record) => (
          <NormalReadOnlyCell
            value={record[BACKEND_RESPONSE_KEYS.FLYFIN_USER_NAME]}
          />
        ),
        backendResponseKey: BACKEND_RESPONSE_KEYS.FLYFIN_USER_NAME,
      },
      {
        label: CPA_LIST_COLUMNS.RETURN_STATUS,
        render: (record: Record) => {
          if (record[BACKEND_RESPONSE_KEYS.BUSINESS_RETURN_DATA].length > 0) {
            return (
              <div
                style={{
                  backgroundColor: themmeColor.cpaCenterV2Bg,
                  borderRadius: 12,
                  paddingBlock: 8,
                  paddingInline: 12,
                }}>
                Multiple Returns (
                {record[BACKEND_RESPONSE_KEYS.BUSINESS_RETURN_DATA].length + 1}
                )
              </div>
            );
          }
          return (
            <UpdateReturnStatusCell
              currentReturnStatusId={record[BACKEND_RESPONSE_KEYS.STATUS]}
              taxFilingId={record[BACKEND_RESPONSE_KEYS.TAX_FILING_ID]}
              onSuccess={fetchUserList}
            />
          );
        },
        backendResponseKey: BACKEND_RESPONSE_KEYS.STATUS,
      },
      {
        label: CPA_LIST_COLUMNS.CPA,
        render: (record: Record) => {
          return (
            <CPAAssignCell
              fieldId={CPA_LIST_COLUMNS.CPA}
              taxFilingId={record[BACKEND_RESPONSE_KEYS.TAX_FILING_ID]}
              value={
                record[BACKEND_RESPONSE_KEYS.CPA]
                  ? record[BACKEND_RESPONSE_KEYS.CPA].split('@')[0]
                  : record[BACKEND_RESPONSE_KEYS.CPA]
              }
              userId={record[BACKEND_RESPONSE_KEYS.FLYFIN_USER_ID]}
              fetchList={fetchUserList}
              isAssigned={![null].includes(record[BACKEND_RESPONSE_KEYS.CPA])}
            />
          );
        },
        backendResponseKey: BACKEND_RESPONSE_KEYS.CPA,
      },
      {
        label: CPA_LIST_COLUMNS.PLANS,
        render: (record: Record) => (
          <div style={{display: 'flex', alignItems: 'flex-end'}}>
            <NormalReadOnlyCell
              value={record[BACKEND_RESPONSE_KEYS.PAYMENT_PLAN]}
            />
            {record[BACKEND_RESPONSE_KEYS.ELIGIBILITY] == 'Not Eligible' && (
              <div style={{marginLeft: '2px'}}>
                <FilledCrossIcon height={16} width={16} />
              </div>
            )}
          </div>
        ),
        backendResponseKey: BACKEND_RESPONSE_KEYS.PAYMENT_PLAN,
      },
      {
        label: CPA_LIST_COLUMNS.CPA_ACTION,
        render: (record: Record) => {
          const action: ACTION_TYPES = record[BACKEND_RESPONSE_KEYS.CPA_ACTION]
            ? ACTION_TYPES.YES
            : ACTION_TYPES.NO;
          return (
            <div style={{display: 'flex', alignItems: 'center'}}>
              <ActionCell value={action} />
              {record[BACKEND_RESPONSE_KEYS.TOTAL_OPEN_QUERIES] > 0 && (
                <div style={{marginLeft: '8px'}}>
                  <FractionCell
                    isActionable={
                      record[BACKEND_RESPONSE_KEYS.OPEN_QUERIES_ON_CPA] > 0
                    }
                    num={record[BACKEND_RESPONSE_KEYS.OPEN_QUERIES_ON_CPA]}
                    den={record[BACKEND_RESPONSE_KEYS.TOTAL_OPEN_QUERIES]}
                  />
                </div>
              )}
            </div>
          );
        },
        backendResponseKey: BACKEND_RESPONSE_KEYS.CPA_ACTION,
      },
      {
        label: CPA_LIST_COLUMNS.DOCS_SUBMITTED,
        render: (record: Record) => (
          <NormalReadOnlyCell
            value={
              record[BACKEND_RESPONSE_KEYS.DOCS_SUBMITTED]
                ? getMonthDateFromDate(
                    getDateTimeFromMonthDayString(
                      record[BACKEND_RESPONSE_KEYS.DOCS_SUBMITTED],
                    ) as Date,
                  )
                : 'null'
            }
          />
        ),
        backendResponseKey: BACKEND_RESPONSE_KEYS.LAST_STATUS_CHANGE,
      },
      {
        label: CPA_LIST_COLUMNS.NOTE,
        render: (record: Record) => (
          <UpdateNoteCell
            value={record[BACKEND_RESPONSE_KEYS.NOTE]}
            whom={'Varsha'}
            timeStamp={1678350076}
            isEmpty={
              !record[BACKEND_RESPONSE_KEYS.NOTE] ||
              record[BACKEND_RESPONSE_KEYS.NOTE].length === 0
            }
            taxFilingId={record[BACKEND_RESPONSE_KEYS.TAX_FILING_ID]}
            onSuccess={fetchUserList}
          />
        ),
        backendResponseKey: BACKEND_RESPONSE_KEYS.NOTE,
      },
      {
        label: CPA_LIST_COLUMNS.ACTIVITY,
        render: (record: Record) => {
          return (
            <ActivityCell
              activityStatus={
                record[BACKEND_RESPONSE_KEYS.ACTIVITY] as USER_ACTIVITY_TYPE
              }
              taxFilingId={record[BACKEND_RESPONSE_KEYS.TAX_FILING_ID]}
              userId={record[BACKEND_RESPONSE_KEYS.FLYFIN_USER_ID]}
              fetchUserList={fetchUserList}
            />
          );
        },
        backendResponseKey: BACKEND_RESPONSE_KEYS.ACTIVITY,
      },
      {
        label: CPA_LIST_COLUMNS.REVIEWER,
        render: (record: Record) => (
          <CPAAssignCell
            fieldId={CPA_LIST_COLUMNS.REVIEWER}
            value={
              record[BACKEND_RESPONSE_KEYS.CPA_REVIEWER]
                ? record[BACKEND_RESPONSE_KEYS.CPA_REVIEWER].split('@')[0]
                : record[BACKEND_RESPONSE_KEYS.CPA_REVIEWER]
            }
            userId={record[BACKEND_RESPONSE_KEYS.FLYFIN_USER_ID]}
            fetchList={fetchUserList}
            isAssigned={
              ![null, VALID_CPA_EMAILS.Unassigned].includes(
                record[BACKEND_RESPONSE_KEYS.CPA_REVIEWER],
              )
            }
          />
        ),
        backendResponseKey: BACKEND_RESPONSE_KEYS.CPA_REVIEWER,
      },
      {
        label: CPA_LIST_COLUMNS.REVIEWER_ACTION,
        render: (record: Record) => {
          const action: ACTION_TYPES = record[
            BACKEND_RESPONSE_KEYS.REVIEWER_ACTION
          ]
            ? ACTION_TYPES.YES
            : ACTION_TYPES.NO;
          return <ActionCell value={action} />;
        },
        backendResponseKey: BACKEND_RESPONSE_KEYS.REVIEWER_ACTION,
      },
      // {
      //   label: CPA_LIST_COLUMNS.NEXT_ACTION_DATE,
      //   render: (record: Record) => {
      //     return (
      //       <NextActionDatePickerCell
      //         value={
      //           record[BACKEND_RESPONSE_KEYS.NEXT_ACTION_DATE] ??
      //           getDateForNextAction(new Date())
      //         }
      //         onSuccess={fetchUserList}
      //         taxFilingId={record[BACKEND_RESPONSE_KEYS.TAX_FILING_ID]}
      //         isValid={!!record[BACKEND_RESPONSE_KEYS.NEXT_ACTION_DATE]}
      //       />
      //     );
      //   },
      //   backendResponseKey: BACKEND_RESPONSE_KEYS.NEXT_ACTION_DATE,
      // },
      {
        label: CPA_LIST_COLUMNS.COLLABORATOR_EMAIL,
        render: (record: Record) => (
          <CPAAssignCell
            fieldId={CPA_LIST_COLUMNS.COLLABORATOR_EMAIL}
            value={
              record[BACKEND_RESPONSE_KEYS.COLLABORATOR_EMAIL]
                ? record[BACKEND_RESPONSE_KEYS.COLLABORATOR_EMAIL].split(
                    '@',
                  )[0]
                : record[BACKEND_RESPONSE_KEYS.COLLABORATOR_EMAIL]
            }
            userId={record[BACKEND_RESPONSE_KEYS.FLYFIN_USER_ID]}
            taxFilingId={record[BACKEND_RESPONSE_KEYS.TAX_FILING_ID]}
            fetchList={fetchUserList}
            isAssigned={
              ![null].includes(
                record[BACKEND_RESPONSE_KEYS.COLLABORATOR_EMAIL],
              )
            }
          />
        ),
        backendResponseKey: BACKEND_RESPONSE_KEYS.COLLABORATOR_EMAIL,
      },
    ];

    columns.push(...defaultColumns);

    return columns;
  };

  const cpaListColumnComponentMapper = getCpaListColumnComponentMapper();

  const defaultPlansString = useMemo(
    () => JSON.stringify(defaultPlansKey),
    [defaultPlansKey],
  );

  const defaultSelectedReturnStatusString = useMemo(
    () => JSON.stringify(defaultSelectedReturnStatus),
    [defaultSelectedReturnStatus],
  );

  const defaultSelectedCpaActionsString = useMemo(
    () => JSON.stringify(defaultSelectedCpaActions),
    [defaultSelectedCpaActions],
  );
  const defaultSelectedReviewerActionsString = useMemo(
    () => JSON.stringify(defaultSelectedReviewerActions),
    [defaultSelectedReviewerActions],
  );
  const defaultSelectedCPAsString = useMemo(
    () => JSON.stringify(defaultSelectedCPAs),
    [defaultSelectedCPAs],
  );
  const defaultSelectedReviewersString = useMemo(
    () => JSON.stringify(defaultSelectedReviewers),
    [defaultSelectedReviewers],
  );
  const defaultSelectedDatesString = useMemo(
    () => JSON.stringify(defaultSelectedDates),
    [defaultSelectedDates],
  );

  const defaultSelectedTaxExtensionString = useMemo(
    () => JSON.stringify(defaultSelectedTaxExtension),
    [defaultSelectedTaxExtension],
  );
  const defaultSelectedCollaboratorString = useMemo(
    () => JSON.stringify(defaultSelectedCollaborator),
    [defaultSelectedCollaborator],
  );
  const defaultSelectedTFSegmentString = useMemo(
    () => JSON.stringify(defaultSelectedTFSegment),
    [defaultSelectedTFSegment],
  );

  const incrementPageCount = () => {
    // @ts-ignore
    setSearchParams((prev) => ({
      ...parse(prev.toString()),
      pageCount: pageCount + 1,
    }));
  };

  const decrementPageCount = () => {
    if (pageCount <= 1) return;
    // @ts-ignore
    setSearchParams((prev) => ({
      ...parse(prev.toString()),
      pageCount: pageCount - 1,
    }));
  };

  const goToFirst = () => {
    if (pageCount <= 1) return;
    // @ts-ignore
    setSearchParams((prev) => ({
      ...parse(prev.toString()),
      pageCount: 1,
    }));
  };

  const goToLast = () => {
    if (pageCount >= totalPages) return;
    // @ts-ignore
    setSearchParams((prev) => ({
      ...parse(prev.toString()),
      pageCount: totalPages,
    }));
  };

  const onClickSelect = () => {
    setSelectUsersForAction((prev) => !prev);
    setSelectedUsersList([]);
    setShowCpaAckMessageDrawer(false);
    setShowFileExtensionConfirmation(false);
  };

  const onExportExtensionData = async () => {
    setIsLoading(true);
    try {
      await exportExpressExtensionData({year: activeYear});
      notify(`Export extension data success`, NotificationType.success);
    } catch (e) {
      notify(`Export extension data failed ${e}`, NotificationType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const onUpdateExtensionStatus = async () => {
    try {
      setIsLoading(true);
      await updateTaxFilingExtensionStatus({
        fly_user_id_list: selectedUsersList,
        year: activeYear,
        tax_extension_status: TAX_EXTENSION_STATUS.FILED,
      });
      await fetchUserList();
      setShowFileExtensionConfirmation(false);
      setSelectUsersForAction(false);
      notify('Extension status updated', NotificationType.success);
    } catch (e) {
      notify(`Extension status update failed ${e}`, NotificationType.error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserList();
  }, [
    defaultReverse,
    defaultSortingKey,
    defaultPlansString,
    defaultSelectedReturnStatusString,
    defaultSelectedCpaActionsString,
    defaultSelectedReviewerActionsString,
    defaultSelectedCPAsString,
    defaultSelectedReviewersString,
    defaultSelectedDatesString,
    defaultSelectedTaxExtensionString,
    defaultUserId,
    defaultEmail,
    defaultName,
    pageCount,
    defaultSelectedActivity,
    defaultSelectedTaxQueryStatus,
    defaultSelectedCommsMode,
    defaultSelectedExpressExtensionEligible,
    defaultSelectedCollaboratorString,
    defaultSelectedTFSegmentString,
    defaultSelectedFiledElsewhere,
    activeYear,
  ]);

  return (
    <CpaListContext.Provider
      value={{
        fetchUserList,
      }}>
      <div style={{height: windowSize.height, overflow: 'hidden'}}>
        {/* FILTERS */}
        <div style={{height: NAV_BAR_HEIGHT}}>
          <FilterBar
            title={`ReturnCentral - RC`}
            disableSearchPanel={isLoading}
            onClearFilter={onClearFilter}
            onClickHamburger={openDrawer}
            onClickSearch={onClickSearchHandler}
            init={{
              userId: defaultUserId ?? '',
              email: defaultEmail ?? '',
              name: defaultName ?? '',
            }}>
            <div style={{marginTop: 10}}>
              <ExportColumns getFilterParams={getFilterParams} />
              <Button
                onClick={openFilterModal}
                variant="outlined"
                disabled={isLoading}
                style={{
                  color: themmeColor.grey,
                  borderColor: themmeColor.grey,
                  borderWidth: 1,
                  marginLeft: 10,
                }}>
                Filters
              </Button>
              {/* CPA ACKNOWLEDGEMENT */}
              <DSButton
                onClick={onClickSelect}
                type={selectUsersForAction ? 'primary' : 'secondary'}
                disabled={isLoading}
                text={selectUsersForAction ? 'Cancel selection' : 'Select'}
                style={{marginLeft: 20}}
              />
              {/* EXPORT EXPRESS EXTENSION DATA */}
              <DSButton
                onClick={onExportExtensionData}
                type={'secondary'}
                disabled={isLoading}
                text={'Export extension data'}
                style={{marginLeft: 20}}
              />
            </div>
          </FilterBar>
        </div>
        {/* MAIN CONTENT */}
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: MAIN_CONTENT_HEIGHT,
            }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{height: MAIN_CONTENT_HEIGHT}}>
            <TableContainer style={{height: USER_LIST_HEIGHT}}>
              <Table stickyHeader>
                <ListTableHeader tableData={cpaListColumnComponentMapper} />
                <ListTableBody
                  isTaxFilingTable
                  recordsArray={recordsArray}
                  tableData={cpaListColumnComponentMapper}
                  getRedirectRoute={({flyfin_user_id}) =>
                    flyfin_user_id.toString()
                  }
                  flyUserIdFieldName={BACKEND_RESPONSE_KEYS.FLYFIN_USER_ID}
                  cpaReturnStatusFieldName={
                    BACKEND_RESPONSE_KEYS.CPA_FACING_RETURN_STATUS
                  }
                />
              </Table>
            </TableContainer>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: selectUsersForAction
                  ? 'space-between'
                  : 'flex-end',
                alignItems: 'center',
                paddingLeft: 10,
                paddingRight: 10,
                backgroundColor: themmeColor.offWhite,
                height: FOOTER_HEIGHT,
              }}>
              {selectUsersForAction && (
                <div>
                  <DSButton
                    type="primary"
                    text="File Extension"
                    disabled={isLoading || selectedUsersList.length === 0}
                    onClick={() => setShowFileExtensionConfirmation(true)}
                  />
                  <DSButton
                    type="secondary"
                    text="Send acknowledgement"
                    disabled={isLoading || selectedUsersList.length === 0}
                    onClick={() => setShowCpaAckMessageDrawer(true)}
                    style={{marginLeft: 10}}
                  />
                </div>
              )}
              {/* PAGINATION */}
              <Pagination
                isPrev={isPrev}
                isNext={isNext}
                isLoading={isLoading}
                pageCount={pageCount}
                totalPages={totalPages}
                onDecrementPage={decrementPageCount}
                onGotoFirstPage={goToFirst}
                onGotoLastPage={goToLast}
                onIncrementPage={incrementPageCount}
              />
            </div>
          </div>
        )}
      </div>
      {isFilterModalOpen && (
        <CpaListFilterModal
          isOpen={isFilterModalOpen}
          onClose={closeFilterModal}
          tableData={cpaListColumnComponentMapper}
        />
      )}
      {showCpaAckMessageDrawer && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            zIndex: 100,
          }}>
          <CpaAcknowledgementDrawer
            isVisible={showCpaAckMessageDrawer}
            selectedUsers={selectedUsersList}
            onClose={() => setShowCpaAckMessageDrawer(false)}
            onSendSideEffect={onClickSelect}
          />
        </div>
      )}
      <ConfirmFileExtensionPopUp
        key={`confirm-extension-${showFileExtensionConfirmation}`}
        isOpen={showFileExtensionConfirmation}
        onClose={() => setShowFileExtensionConfirmation(false)}
        tableData={selectedUsersList.map((id) => ({
          userId: id,
          userName:
            recordsArray.find(
              (record) => record[BACKEND_RESPONSE_KEYS.FLYFIN_USER_ID] === id,
            )?.[BACKEND_RESPONSE_KEYS.FLYFIN_USER_NAME] ?? '',
        }))}
        loading={isLoading}
        onCofirm={onUpdateExtensionStatus}
      />
    </CpaListContext.Provider>
  );
};

export default CpaCenterList;
